import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import Keycloak from "keycloak-js";
import type { UserContext, SystemRole } from "./rbac";
import { API_BASE, APP_BASE, appUrl } from "./utils";
import { authFetch, initAuthFetch, setStaticToken, setImpersonateOrg, setAccessExpiredHandler } from "./fetch";
import {
  identifyUser as identifyPostHogUser,
  updateAnalyticsContext,
  resetPostHog,
  capture as capturePostHogEvent,
} from "./posthog";
import { queryClient } from "./query-client";
import { waitForOrgClaim } from "./org-claim";
import { resolveActiveOrg } from "./active-org";
import { sweepStaleCallbackState } from "@/lib/keycloak-callback-cleanup";
import { resolveOrgStatus, type OrgStatus } from "./org-status";

const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
// AUTH_MODE: "local" → local Keycloak, "gcp" → shared dev instance
const AUTH_MODE = import.meta.env.VITE_AUTH_MODE || "gcp";
const KEYCLOAK_URLS: Record<string, string> = {
  local: "http://localhost:8080/auth",
  gcp: "https://dev.gcp.fastn.ai/auth",
};
export const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL || KEYCLOAK_URLS[AUTH_MODE] || (isLocalhost ? "http://localhost:8080/auth" : "/auth");
export const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM ?? "fastn";
const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? "fastn-app";


const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === "true";

// PREVIEW_MODE bypasses Keycloak auth — triple-gated, all must be true:
// 1. Claude Preview browser (user agent contains "Claude/")
// 2. Non-production Vite build (compile-time, cannot be spoofed at runtime)
// 3. Running on localhost (not any deployed domain)
const IS_CLAUDE_BROWSER = /\bClaude\//.test(navigator.userAgent);
const IS_LOCALHOST = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
const PREVIEW_MODE = IS_CLAUDE_BROWSER && IS_LOCALHOST && import.meta.env.MODE !== "production";

/** Bounded retry for the memberships fetch — see membershipAttempt. Linear
 *  backoff, so a new account whose org is mid-provision gets ~1.5s, 3s and 4.5s
 *  before we stop waiting for an org that may legitimately never arrive. */
const MEMBERSHIP_MAX_ATTEMPTS = 3;
const MEMBERSHIP_RETRY_BASE_MS = 1_500;

// SCOPED LINK TOKEN: hand-off URLs (connect / config-review / test-case-review)
// arrive with a `#t=emb_...` fragment carrying a short-lived (20 min) scoped
// embed token. When present, the recipient authenticates off that token alone —
// no Keycloak login — exactly like AUTH_DISABLED's static-token path. Only a
// well-formed emb_ token triggers the bypass; anything else falls through to
// normal login. Captured once at module load before the fragment is stripped.
const LINK_TOKEN: string | null = (() => {
  if (typeof window === "undefined") return null;
  const m = window.location.hash.match(/[#&]t=(emb_[A-Za-z0-9_-]+)/);
  return m ? m[1] : null;
})();

interface ImpersonationState {
  orgId: string;
  accountName: string;
  originalToken: string;
  originalUserContext: UserContext;
  expiresAt: number; // unix ms
}

export interface Membership {
  id: string;
  orgId: string;
  accountName: string;  // org name
  accountSlug: string;  // org slug
  orgType: string;      // company, personal, partner, end_org, platform
  // null for partner/account-level orgs (the SaaS partner). Non-null when
  // this membership is for a tenant workspace nested under a partner — used
  // to hide partner-only surfaces (Widget Builder, hub config) from tenant
  // admins, matching the Paragon / Merge / Workato embedded model.
  parentOrgId: string | null;
  orgDomain: string | null; // for favicon lookup
  // Commercial tier of this org. Display and analytics segmentation only —
  // every entitlement and quota decision is made server-side against
  // orgs.plan, never against this.
  plan: string | null;
  role: SystemRole;
  isDefault: boolean;
  // ISO timestamp, or null for access that does not expire. Past this instant
  // the API refuses every request for this org, so the switcher must not offer
  // it — see getSwitchableAccounts.
  expiresAt: string | null;
}

interface AuthContextValue {
  ready: boolean;
  authenticated: boolean;
  /** See the derivation in AuthProvider — true while org/role are still a guess. */
  contextPending: boolean;
  token: string | undefined;
  user: { email?: string; name?: string; sub?: string } | undefined;
  userContext: UserContext | null;
  memberships: Membership[];
  pendingMemberships: Membership[];
  /** Every membership is past its expiry — the API refuses this user everywhere. */
  accessExpired: boolean;
  // Per-admin toggle: whether the logged-in platform admin may see and switch
  // into the platform (org_platform) org in the account switcher. false for
  // everyone else. Sourced from GET /api/v1/users/me/memberships.
  canSwitchPlatformOrg: boolean;
  /**
   * Whether this session has an org to act as. Nothing is scoped without one,
   * so a session that resolves none can only reach 401s — see ./org-status.
   */
  orgStatus: OrgStatus;
  /** Re-run the memberships fetch. Backs the retry on the no-workspace screen. */
  retryMemberships: () => void;
  /**
   * Re-reads /users/me/memberships in-session. The org name renders from this
   * list, so a rename has to refresh it or the rail keeps the old name until
   * the next full page load.
   *
   * Distinct from retryMemberships on purpose: that one clears
   * membershipsLoaded to drive the no-workspace screen's retry, which would
   * flash a loading state after a rename. This re-reads silently.
   */
  refreshMemberships: () => Promise<void>;
  impersonating: ImpersonationState | null;
  login: () => void;
  // Async: revokes the access token server-side before handing off to Keycloak.
  // Safe to call as a bare onClick handler — callers need not await it.
  logout: () => Promise<void>;
  switchAccount: (orgId: string) => Promise<void>;
  startImpersonation: (orgId: string, accountName: string) => Promise<void>;
  startUserImpersonation: (userId: string, userName: string) => Promise<void>;
  stopImpersonation: () => void;
  openAccountSecurity: (action?: 'CONFIGURE_TOTP' | 'webauthn-register-passwordless') => void;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const keycloak = PREVIEW_MODE
  ? null
  : new Keycloak({ url: KEYCLOAK_URL, realm: KEYCLOAK_REALM, clientId: KEYCLOAK_CLIENT_ID });

/* The init PROMISE, not a "have we started" boolean.
   keycloak.init() may only be called once per Keycloak instance, so the call
   has to be guarded either way. Keeping the promise means a second mount can
   await the same init and resolve its own `ready`, instead of returning early
   and leaving it false forever. See the effect below. */
let keycloakInitPromise: Promise<boolean> | null = null;

// Security: Tokens must NOT be stored in localStorage. localStorage is accessible to any
// JavaScript running on the page, making it vulnerable to XSS attacks. Tokens should
// only be held in React state (memory) so they are cleared on page close/refresh.

function extractUserContext(parsed: Record<string, unknown> | undefined): UserContext | null {
  if (!parsed) return null;

  // Role comes from 'role' user attribute mapped to JWT claim.
  // If missing, default to viewer (least privilege) — but record that the value
  // is a fallback, not something the token said. A brand-new signup's token is
  // minted before provisioning writes the role onto the Keycloak user, so this
  // branch is exactly what a first-run owner hits, and rendering the guess as
  // though it were the answer is what put them in a read-only dashboard.
  const roleClaim = typeof parsed.role === 'string' && parsed.role ? (parsed.role as SystemRole) : undefined;
  const role: SystemRole = roleClaim ?? 'viewer';

  const orgId = (parsed.org_id as string | undefined) ?? '';
  const sub = (parsed.sub as string | undefined) ?? '';
  const email = (parsed.email as string | undefined) ?? '';
  return {
    sub,
    email,
    role,
    orgId,
    endOrgId: parsed.end_org_id as string | undefined,
    roleResolved: roleClaim !== undefined,
    accountName: parsed.org_name as string | undefined,
    displayName: (parsed.name as string | undefined) || (parsed.preferred_username as string | undefined),
  };
}

/**
 * Drop every cached response on an auth-context transition.
 *
 * `switchOrg` has always done this inline. Entering and leaving impersonation
 * are the same kind of transition and did not, which left the previous
 * context's responses in the cache for `gcTime`.
 *
 * Keys carry orgId, so most entries could only go stale, not be mis-served.
 * The exception is a response whose CONTENT varies by something the key does
 * not carry: GET /api/v1/actions resolves createdBy/updatedBy actors for
 * `platform_admin` and for nobody else. `keys.actions` now carries role and sub
 * for that reason, and this is the other half — the two are deliberately
 * belt-and-braces, because the next role-dependent payload will not come with a
 * reminder to widen a key.
 *
 * Cancel before clear: an in-flight request started under the old context would
 * otherwise resolve into the new one's cache.
 */
async function dropCachedData(): Promise<void> {
  await queryClient.cancelQueries();
  queryClient.clear();
}

export function AuthProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [ready, setReady] = useState(PREVIEW_MODE);
  const [authenticated, setAuthenticated] = useState(false);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [user, setUser] = useState<AuthContextValue["user"]>(undefined);
  const [userContext, setUserContext] = useState<UserContext | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [pendingMemberships, setPendingMemberships] = useState<Membership[]>([]);
  const [canSwitchPlatformOrg, setCanSwitchPlatformOrg] = useState(false);
  const [impersonating, setImpersonating] = useState<ImpersonationState | null>(null);
  const impersonationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic id for the memberships effect; only the newest run may write.
  const membershipsRunRef = useRef(0);

  // DEV MODE: keep token in sync with userContext role
  useEffect(() => {
    if (!PREVIEW_MODE || !userContext) return;
    const expectedToken = userContext.role === "platform_admin" ? "fastn_platform_admin" : "fastn_dev_user";
    if (token !== expectedToken) setToken(expectedToken);
  }, [userContext?.role, token]);

  // Fetch memberships when authenticated — always from real API
  // Track whether initial membership load is done to prevent premature rendering
  const [membershipsLoaded, setMembershipsLoaded] = useState(PREVIEW_MODE);
  // Every membership this user has is past its expires_at — the API refuses
  // them everywhere, so the app must render a terminal state, not its chrome.
  const [accessExpired, setAccessExpired] = useState(false);

  // Any 403 carrying the expiry code flips this, not just the memberships
  // fetch — an expiry that passes mid-session would otherwise go unnoticed
  // until the next token refresh re-ran that one request.
  useEffect(() => {
    setAccessExpiredHandler(() => setAccessExpired(true));
    return () => setAccessExpiredHandler(null);
  }, []);
  /**
   * Retry counter for the memberships fetch.
   *
   * The fetch below keys on [token, authenticated], so it ran exactly once. A
   * brand-new account calls it while its org is still being provisioned, gets a
   * response with no membership carrying an org, and orgId stays "" — with
   * nothing to re-run it. Every query gated on `enabled: !!orgId` then never
   * fires, which is why the setup page showed an empty connector catalog with
   * Continue and Detect systems disabled, and why reloading fixed it.
   */
  const [membershipAttempt, setMembershipAttempt] = useState(0);
  /**
   * Whether the last memberships read failed outright (transport error or a
   * non-2xx body).
   *
   * This used to be swallowed on both branches — the non-ok branch resolved to
   * null, and the catch was commented "non-critical". It is the opposite:
   * it is the only thing that supplies orgId, and without one every scoped
   * request 401s. Swallowing it made a failed read indistinguishable from an
   * account that genuinely has no org, so the app could not tell the user which
   * had happened, and told them neither.
   */
  const [membershipFailed, setMembershipFailed] = useState(false);

  /* Also called after a rename: the org name renders from memberships (the rail
     footer and the account switcher both read it there), and this is the only
     fetch of it in the session. */
  const refreshMemberships = useCallback((): Promise<void> => {
    // Scoped link-token sessions are focused hand-offs (connect / config-review /
    // test-case-review). They aren't permitted for the membership API.
    if (!token || LINK_TOKEN) return Promise.resolve();

    // Which run of this callback owns the write. It fires on the 30s proactive
    // updateToken, on initAuthFetch's 401 retry, at the end of switchAccount, and
    // now explicitly after a rename -- so two fetches can be in flight and the LAST
    // to resolve is not the current one. A response carrying the previous token's
    // org would otherwise be written back over the org the user just switched to:
    // the same "sidebar names one org, requests serve another" symptom this PR
    // exists to remove, arriving from the other direction.
    const runId = ++membershipsRunRef.current;
    const superseded = () => membershipsRunRef.current !== runId;

    return authFetch(`${API_BASE}/api/v1/users/me/memberships`, {}, token)
      .then(async (res) => {
        if (superseded()) return;
        // A caller whose every membership has expired is refused by the gateway
        // on this route like any other. Without reading the code here the 403
        // would be swallowed into "no memberships", and the user would land in
        // the full app chrome with every panel erroring. See AccessExpiredPage.
        if (res.status === 403) {
          const body = await res.json().catch(() => null) as { code?: string } | null;
          if (body?.code === "membership_expired") setAccessExpired(true);
          return null;
        }
        // main's throw, kept: a non-403 failure must reach the catch and set
        // membershipFailed rather than resolving to null and reading as "this
        // user simply has no memberships".
        if (!res.ok) throw new Error(`memberships ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setMembershipFailed(false);
        const mapMembership = (m: Record<string, unknown>): Membership => ({
          id: m.id as string,
          orgId: m.orgId as string,
          accountName: (m.orgName as string) ?? (m.accountName as string) ?? "",
          accountSlug: (m.orgSlug as string) ?? (m.accountSlug as string) ?? "",
          orgType: (m.orgType as string) ?? "company",
          parentOrgId: (m.parentOrgId as string | null) ?? null,
          orgDomain: (m.orgDomain as string) ?? null,
          plan: (m.plan as string | null) ?? null,
          role: (m.role as SystemRole) ?? "end_user",
          isDefault: m.isDefault as boolean,
          expiresAt: (m.expiresAt as string | null) ?? null,
        });
        if (data?.data) {
          const mapped = data.data.map(mapMembership);
          setMemberships(mapped);
          // Not a one-way latch: a user re-invited while the tab is open gets a
          // successful refetch on the next token refresh and must return to the
          // app rather than staying on the expired page.
          setAccessExpired(false);

          // Settle the org and the role against this response. `activeOrgId`
          // carries the org the gateway resolved for this user, so the client
          // reads that decision rather than re-deriving it — see active-org.ts.
          //
          // `role` is deliberately NOT overwritten when the JWT already carried a
          // role claim. This response is a plain JSON body: the VAPT tester
          // intercepted it, changed "viewer" to "admin", and the dashboard
          // rendered the admin surfaces. A signed token cannot be edited that
          // way, so preferring it removes the easy escalation. Membership still
          // supplies the role when the token has none — which is the live path
          // for a platform_admin, whose persona arrives in realm_access rather
          // than a flat claim (see services/api/src/lib/jwt-role.ts), so this
          // narrows the override without changing who resolves to what.
          //
          // This is defence in depth and nothing more. The browser belongs to
          // the attacker; client-side gating can never be authoritative. The
          // real fix is the server-side gates on the endpoints these surfaces
          // call. See docs/qa/vapt-2026-08-remediation.md WS5.
          //
          // Read outside the updater: updaters are called twice under
          // StrictMode and must stay pure.
          const signedInSub = keycloak?.tokenParsed?.sub;
          setUserContext((prev) => {
            if (!prev) return prev;

            // This response describes the signed-in user's own memberships, so
            // it can only speak for that user's org. An impersonation session
            // has deliberately put another subject and another account in
            // context and scopes its requests with x-impersonate-org (which
            // this fetch does not send); reconciling against this body would
            // pull the org back to the admin's own while every other request
            // kept going to the customer's.
            if (signedInSub && prev.sub && prev.sub !== signedInSub) return prev;

            // Adopt the org the gateway resolved for this user, which the
            // response now states outright. Doing this only when the claim was
            // missing (the old `!prev.orgId` condition) left the other half of
            // the account-switch bug in place: a token whose org_id names no
            // active membership is silently overridden by the gateway, so the
            // sidebar kept the phantom account's name while every request came
            // back scoped to the resolved one — an empty trigger list that
            // reads as "deleted", and a 404 for anything opened by id.
            const member = resolveActiveOrg({
              // null and absent are different answers. routes/users.ts nulls
              // this deliberately after the reconcile re-read ("null keeps it
              // on the token's context"); an api predating the field omits it.
              // Folding both into undefined ran the deploy-window shim in the
              // exact case the endpoint exists to decline.
              activeOrgId: typeof data.activeOrgId === "string"
                ? data.activeOrgId
                : (data.activeOrgId === null ? null : undefined),
              claimedOrgId: prev.orgId || undefined,
              memberships: mapped,
            });
            if (!member) return prev;

            const orgHonoured = prev.orgId === member.orgId;
            if (orgHonoured && prev.roleResolved) return prev;

            // Reuse the normalization extractUserContext already did rather
            // than re-reading the raw claim. Reading it again reintroduced
            // exactly the case that motivated normalizing it: a token with
            // role:"" gives roleClaim undefined (so roleResolved is false and
            // this block now runs) but a raw claim of "", which is not
            // nullish — `??` would keep it and write back role:"" marked
            // authoritative. ROLE_HIERARCHY[""] is undefined, so every gate
            // fails and nothing ever revisits it: worse than the viewer
            // fallback this replaced. prev.role already holds the normalized
            // claim whenever roleResolved says the token supplied one.
            const jwtRole = prev.roleResolved ? prev.role : undefined;
            // The org we END UP in decides these — preserving prev's while
            // moving org would pin a name from a different account. That
            // applies to the ROLE too: the token's role belongs to the org the
            // token names, so keeping it while adopting a different org renders
            // the new org with the old org's gates. A stale org_id attribute
            // plus a lower-privileged membership is enough — the gateway stamps
            // the correct role on every request, so the UI would offer controls
            // that then 403.
            return {
              ...prev,
              orgId: member.orgId,
              // platform_admin is the one exception, and it is not a special
              // case invented here -- it is the gateway's own invariant. The
              // role is a REALM role, not org-scoped: server.ts stamps it and
              // says "platform_admin keeps the realm role stamped above --
              // never downgraded", and memberships-cache.ts returns
              // `role: isPlatformAdmin ? null : chosen.role` precisely so the
              // client does not adopt a membership role for one.
              //
              // Without this exemption a prod platform_admin loses the console:
              // they carry no org_id claim (CLAUDE.md rule 19), so prev.orgId
              // is "" and orgHonoured is ALWAYS false. The row that then wins
              // is their is_default membership -- the branch's own API test
              // resolves exactly that to org_belami with role "admin" -- so the
              // UI writes role:"admin" roleResolved:true and every
              // platform_admin gate disappears for the session, while the
              // gateway keeps stamping x-jwt-role: platform_admin on every
              // request. Restores what `jwtRole ?? member.role` did before.
              role: jwtRole === "platform_admin"
                ? jwtRole
                : (orgHonoured ? (jwtRole ?? member.role) : member.role),
              // Authoritative either way now: the claim if the token has one,
              // otherwise the membership row the server just returned.
              roleResolved: true,
              orgType: orgHonoured ? (prev.orgType ?? member.orgType) : member.orgType,
              accountName: orgHonoured ? (prev.accountName ?? member.accountName) : member.accountName,
            };
          });

          // orgType comes ONLY from a membership: the token carries no such
          // claim, so extractUserContext cannot supply it. The repair block
          // above sets it too, but only when the org or the role failed to
          // resolve — which is not a normal session. So on every session whose
          // token carries org_id and a role, orgType stayed undefined, and any
          // gate reading it silently did nothing. Matched on the org we are
          // actually in, so a switched workspace keeps its own type, and it
          // returns `prev` unchanged when nothing moved.
          setUserContext((prev) => {
            if (!prev?.orgId) return prev;
            const current = mapped.find((m: Membership) => m.orgId === prev.orgId);
            if (!current || prev.orgType === current.orgType) return prev;
            return { ...prev, orgType: current.orgType };
          });
        }
        if (data?.pending) {
          setPendingMemberships(data.pending.map(mapMembership));
        }
        setCanSwitchPlatformOrg(data?.canSwitchPlatformOrg === true);
      })
      // Record it instead of discarding it: this decides whether the app can
      // say "this account has no organisation" (a fact) or only "we could not
      // find out" (the truth when the read never landed).
      .catch(() => { if (!superseded()) setMembershipFailed(true); })
      .finally(() => { if (!superseded()) setMembershipsLoaded(true); });
  }, [token, authenticated, membershipAttempt]);

  // Actually run it. refreshMemberships was extracted from this effect so a
  // rename could re-read the list, and the effect that invoked it was lost in a
  // later merge -- leaving the callback declared, exported on the context, and
  // never called. Nothing fetched memberships: membershipsLoaded stayed false,
  // so the retry ladder below returned at its own `!membershipsLoaded` guard,
  // and orgType/role/accountName never settled against a membership.
  //
  // membershipsLoaded is set inside the callback's own finally, so this must not
  // set it again -- except for LINK_TOKEN, where the callback returns before the
  // fetch chain and its finally never runs.
  useEffect(() => {
    if (!token || !authenticated) return;
    if (LINK_TOKEN) {
      setMembershipsLoaded(true);
      return;
    }
    void refreshMemberships();
  }, [token, authenticated, refreshMemberships]);

  // Re-fetch while we still have no org to act as. Bounded: a user whose only
  // memberships are `pending` (awaiting approval) legitimately has no org, so
  // this must not spin forever waiting for something that is not coming.
  useEffect(() => {
    if (PREVIEW_MODE || LINK_TOKEN) return;
    if (!token || !authenticated) return;
    if (!membershipsLoaded) return;          // first attempt still in flight
    if (userContext?.orgId) return;          // resolved — nothing to wait for
    if (membershipAttempt >= MEMBERSHIP_MAX_ATTEMPTS) return;
    const delay = MEMBERSHIP_RETRY_BASE_MS * (membershipAttempt + 1);
    const timer = setTimeout(() => setMembershipAttempt((n) => n + 1), delay);
    return () => clearTimeout(timer);
  }, [token, authenticated, membershipsLoaded, userContext?.orgId, membershipAttempt]);

  const retriesOutstanding = !userContext?.orgId && membershipAttempt < MEMBERSHIP_MAX_ATTEMPTS;
  const orgStatus = resolveOrgStatus({
    bypass: PREVIEW_MODE || AUTH_DISABLED || LINK_TOKEN !== null,
    role: userContext?.role,
    loaded: membershipsLoaded,
    failed: membershipFailed,
    retrying: retriesOutstanding,
    orgId: userContext?.orgId,
    activeCount: memberships.length,
    pendingCount: pendingMemberships.length,
  });

  /**
   * Read memberships again, on demand.
   *
   * Advances the same counter the fetch effect is keyed on, so it always
   * changes and the read always re-fires — a reset to 0 would be a no-op
   * whenever it already was 0. It deliberately does not restore the automatic
   * backoff budget: past it, retrying is the person's call, not a loop's.
   */
  const retryMemberships = useCallback(() => {
    setMembershipsLoaded(false);
    setMembershipFailed(false);
    setMembershipAttempt((n) => n + 1);
  }, []);

  // Keep the analytics envelope in step with the context it describes.
  //
  // identifyUser() runs the moment Keycloak returns, which is BEFORE the
  // memberships response lands — so at that point the workspace, the plan and
  // whether this is a tenant are all still unknown, and the org itself can
  // still be revised by the reconcile block above. All three also change on an
  // account switch and on entering or leaving impersonation.
  //
  // Registering from an effect rather than at the identify call site is what
  // makes those cases correct: an event attributed to the previous org, or to a
  // null workspace, cannot be repaired afterwards — PostHog group membership is
  // not retroactive.
  useEffect(() => {
    if (!userContext) return;
    // A scoped link session already registered its own envelope at identify and
    // must not be re-registered here. This block reads `memberships`, which that
    // path never fetches (see the LINK_TOKEN guard in the memberships effect),
    // so every value it derives would be wrong for it: surface would revert to
    // "dashboard", workspace_id would name the TENANT rather than the partner
    // that minted the link, and tenant_id would go null -- silently undoing the
    // one thing that makes those sessions attributable.
    if (LINK_TOKEN) return;
    const active = memberships.find((m) => m.orgId === userContext.orgId);

    // A tenant is an `end_org` — NOT simply "an org with a parent".
    //
    // Every org except org_platform itself has a parent: a partner account
    // (`company` / `personal`) is parented to org_platform, and only an
    // `end_org` is parented to a partner. Testing parentOrgId for null would
    // therefore label almost every partner account a tenant of the platform and
    // make tenant_id non-null for practically the whole user base, which
    // reduces the north star — active tenant integrations — to noise.
    //
    // This is deliberately NOT uclWorkspace()'s rule, and the difference is not
    // a drift. Server-side, OrgContext.parentOrgId is set only when the token
    // mint rewrote orgId to a tenant, so there it already means "acting as a
    // tenant". The membership row here is the raw org tree, where it does not.
    const isTenant = (active?.orgType ?? userContext.orgType) === "end_org";
    const workspaceId = isTenant ? (active?.parentOrgId ?? userContext.orgId) : userContext.orgId;

    updateAnalyticsContext({
      email: userContext.email,
      role: userContext.role,
      orgId: userContext.orgId,
      accountName: userContext.accountName ?? active?.accountName,
      workspaceId,
      workspaceName: memberships.find((m) => m.orgId === workspaceId)?.accountName,
      tenantId: isTenant ? userContext.orgId : null,
      plan: active?.plan ?? null,
      isImpersonating: impersonating !== null,
    });
  }, [userContext, memberships, impersonating]);

  // Auto-expire impersonation after TTL
  useEffect(() => {
    if (!impersonating) return;
    const remaining = impersonating.expiresAt - Date.now();
    if (remaining <= 0) {
      // Already expired — restore immediately
      doStopImpersonation(impersonating);
      return;
    }
    impersonationTimerRef.current = setTimeout(() => {
      setImpersonating((current) => {
        if (current) doStopImpersonation(current);
        return null;
      });
    }, remaining);
    return () => {
      if (impersonationTimerRef.current) clearTimeout(impersonationTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonating?.expiresAt]);

  // Restores original state from impersonation (no hooks, just setters)
  function doStopImpersonation(state: ImpersonationState) {
    setToken(state.originalToken);
    setUserContext(state.originalUserContext);
    setUser({
      email: state.originalUserContext.email,
      name: state.originalUserContext.displayName,
      sub: state.originalUserContext.sub,
    });
    setImpersonating(null);
    setImpersonateOrg(null);
    if (impersonationTimerRef.current) {
      clearTimeout(impersonationTimerRef.current);
      impersonationTimerRef.current = null;
    }
    dropCachedData();
    // Log stop audit (fire-and-forget)
    fetch(`${API_BASE}/api/v1/impersonate/stop`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${state.originalToken}`,
      },
      body: JSON.stringify({ orgId: state.orgId, reason: "manual" }),
    }).catch(() => { /* best-effort audit */ });
  }

  // AUTH_DISABLED: fetch mock token from API and auto-login
  useEffect(() => {
    if (!AUTH_DISABLED) return;
    fetch(`${API_BASE}/api/v1/auth/mock-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    })
      .then((r) => r.json())
      .then((data: { data: { token: string; claims: Record<string, unknown> } }) => {
        const { token: mockToken, claims } = data.data;
        setStaticToken(mockToken);
        setToken(mockToken);
        setAuthenticated(true);
        setUser({ email: claims.email as string, name: claims.name as string, sub: claims.sub as string });
        const ctx = extractUserContext(claims);
        setUserContext(ctx);
        // Same reason as the PREVIEW_MODE bypass below: without an identify
        // here, _identified stays false and every capture() is a silent no-op,
        // which makes a local build useless for checking that instrumentation
        // actually fires.
        if (ctx) {
          identifyPostHogUser({
            id: ctx.sub,
            email: ctx.email,
            name: claims.name as string | undefined,
            role: ctx.role,
            orgId: ctx.orgId,
            accountName: ctx.accountName,
          });
        }
        setReady(true);
      })
      .catch((err) => {
        console.error("Mock token fetch failed", err);
        // Fallback to hardcoded mock. The /mock-token endpoint has a
        // loopback-only gate (defense in depth against a misconfigured
        // non-prod deploy) — under Podman's rootless NAT the browser's
        // request never appears as 127.0.0.1, so this branch runs on
        // every page load there, not just on genuine network failure.
        // The fallback token must still be a well-formed unsigned JWT:
        // routes that manually decode claims (e.g. /api/v1/onboarding/*)
        // reject a bare placeholder string as "Invalid token" even though
        // AUTH_DISABLED's blanket bypass accepts it elsewhere — a silent,
        // hard-to-diagnose gap between two auth paths that only agree on
        // real deploys where the loopback gate passes.
        const claims = {
          sub: "mock-admin-001",
          email: "admin@fastn.dev",
          role: "platform_admin",
          org_id: "org_acme",
          org_name: "Acme SaaS",
          name: "Mock Admin",
          iss: "fastn-mock-fallback",
          iat: Math.floor(Date.now() / 1000),
          exp: Math.floor(Date.now() / 1000) + 365 * 24 * 60 * 60,
        };
        const base64url = (obj: unknown) =>
          btoa(JSON.stringify(obj)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
        const fallbackToken = `${base64url({ alg: "none", typ: "JWT" })}.${base64url(claims)}.`;
        setStaticToken(fallbackToken);
        setToken(fallbackToken);
        setAuthenticated(true);
        setUser({ email: claims.email, name: claims.name, sub: claims.sub });
        setUserContext({
          sub: claims.sub,
          email: claims.email,
          role: "platform_admin",
          roleResolved: true,
          orgId: claims.org_id,
          accountName: claims.org_name,
          displayName: claims.name,
        });
        identifyPostHogUser({
          id: claims.sub,
          email: claims.email,
          name: claims.name,
          role: "platform_admin",
          orgId: claims.org_id,
          accountName: claims.org_name,
        });
        setReady(true);
      });
  }, []);

  // SCOPED LINK TOKEN: authenticate off the fragment token, no Keycloak. Mirrors
  // the AUTH_DISABLED static-token path. The token alone authorizes the focused
  // page's API calls; we also validate it to populate user context for display.
  useEffect(() => {
    if (!LINK_TOKEN) return;
    setStaticToken(LINK_TOKEN);
    setToken(LINK_TOKEN);
    setAuthenticated(true);
    // Strip the token from the URL so it doesn't linger in history / address bar.
    try {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    } catch { /* non-fatal */ }
    fetch(`${API_BASE}/api/v1/embed/token/validate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: LINK_TOKEN }),
    })
      .then((r) => r.json())
      .then((data: { data?: { valid: boolean; orgId: string; endOrgId?: string; userId: string; userEmail?: string; userName?: string } }) => {
        const d = data.data;
        if (d?.valid) {
          setUser({ email: d.userEmail, name: d.userName, sub: d.userId });
          setUserContext({
            sub: d.userId,
            email: d.userEmail ?? "",
            role: "end_user",
            roleResolved: true,
            // The org this session ACTS AS, which for a tenant-scoped link is the
            // tenant, not the partner that minted it. `validate` answers with both
            // (embed.ts) and the gateway stamps x-jwt-org-id from endOrgId when it
            // is set, so reading only `orgId` made userContext disagree with every
            // request the same page sent. That is not cosmetic: ConnectModal sends
            // `endOrgId: previewTenantId ?? userContext.orgId`, so a setup-link
            // connect named the PARTNER as its end-org and the credential landed in
            // the partner's own pool (workspace = endOrg = partner) instead of the
            // tenant's segment — invisible to the tenant's own installation.
            // A link with no tenant (config-review, test-case-review, a
            // partner-level connect link) has endOrgId "" and is unaffected.
            orgId: d.endOrgId || d.orgId,
            // Kept distinct so a surface that needs "which tenant" can ask for it
            // without inferring it from orgId, matching the Keycloak path's shape.
            endOrgId: d.endOrgId || undefined,
            accountName: d.userName ?? "",
            displayName: d.userName ?? "",
          });
          // Identify, or this session measures NOTHING. capture() and
          // capturePageview() both no-op until _identified is true, and this
          // path never went through the Keycloak block that sets it -- so every
          // setup-link session emitted zero events while looking instrumented.
          //
          // surface is "setup_link", not "dashboard": same bundle, different
          // product. The audience is a partner's end customer doing one focused
          // task from an emailed link, and folding them into the operator
          // numbers is what made setup-link completion unmeasurable.
          //
          // The org identity here follows userContext above -- orgId is the org
          // being ACTED IN (the tenant for a tenant-scoped link), and the
          // partner that minted the link is the workspace, which is the unit
          // that renews. A partner-level link has no endOrgId and both collapse
          // to the same org, which is correct.
          identifyPostHogUser({
            id: d.userId,
            email: d.userEmail,
            name: d.userName,
            role: "end_user",
            surface: "setup_link",
            orgId: d.endOrgId || d.orgId,
            workspaceId: d.orgId,
            tenantId: d.endOrgId || null,
            accountName: d.userName ?? undefined,
          });
        }
      })
      .catch(() => { /* token still authorizes API calls even if validate fails */ })
      .finally(() => setReady(true));
  }, []);

  useEffect(() => {
    if (PREVIEW_MODE || AUTH_DISABLED || LINK_TOKEN || !keycloak) return;
    const initTimeout = setTimeout(() => {
      console.warn("Keycloak init timed out — showing login page");
      setReady(true);
    }, 5000);

    /* The guard used to be `if (keycloakInitialized) return;` -- and that
       `return` skipped setReady. The guard is module state; `ready` is
       component state. So any remount of this provider after the first init
       left ready === false with nothing remaining to flip it: a permanent
       "Loading..." on a dark background. And because init never ran a second
       time, the OAuth state/code fragment was never parsed out of the URL
       either. A full reload resets module state, which is exactly why
       re-navigating cleared it.
       Awaiting the stored promise keeps init single-flight AND lets every
       mount resolve its own ready. */
    if (!keycloakInitPromise) {
      keycloakInitPromise = keycloak.init({ onLoad: "login-required", pkceMethod: "S256", checkLoginIframe: false });
    }
    keycloakInitPromise
      .then((auth) => {
        clearTimeout(initTimeout);
        // keycloak-js only prunes expired kc-callback-* entries when it happens
        // to be parsing a callback, so abandoned login attempts pile up. Ours
        // has just been consumed, so anything expired left over is dead weight
        // -- including a PKCE code_verifier we would rather not keep.
        sweepStaleCallbackState();
        setAuthenticated(auth);
        if (auth) {
          setToken(keycloak.token);
          const parsed = keycloak.tokenParsed;
          setUser({
            email: parsed?.email as string | undefined,
            name: parsed?.name as string | undefined,
            sub: parsed?.sub,
          });
          const ctx = extractUserContext(parsed as Record<string, unknown> | undefined);
          setUserContext(ctx);
          if (ctx) {
            identifyPostHogUser({
              id: ctx.sub,
              email: ctx.email,
              name: (parsed?.name as string | undefined),
              role: ctx.role,
              orgId: ctx.orgId,
              accountName: ctx.accountName,
            });
            // Distinguish a real login from a session resume. This block
            // re-runs on every full page load, so `auth_time <= 60s` alone
            // counts reloads — and silent token re-auth — of a live session
            // as logins (KAN-1819). Also gate on the Keycloak session id:
            // emit user_logged_in at most once per `sid`. `sid` is stable
            // across reloads / token refresh / silent SSO re-auth within one
            // session but changes on a genuine re-login, so a reload of an
            // already-counted session falls through to user_session_resumed.
            // Falls back to auth_time alone when `sid` is absent.
            const authTimeSec = parsed?.auth_time as number | undefined;
            const secondsSinceAuth = typeof authTimeSec === "number"
              ? Math.floor(Date.now() / 1000) - authTimeSec
              : Number.MAX_SAFE_INTEGER;
            const sid = parsed?.sid as string | undefined;
            let sidAlreadyCounted = false;
            if (sid) {
              try { sidAlreadyCounted = localStorage.getItem("ph_login_sid") === sid; } catch { /* storage unavailable */ }
            }
            const isFreshLogin = secondsSinceAuth <= 60 && !sidAlreadyCounted;
            if (isFreshLogin && sid) {
              try { localStorage.setItem("ph_login_sid", sid); } catch { /* storage unavailable */ }
            }
            const baseProps = {
              email: ctx.email,
              role: ctx.role,
              org_id: ctx.orgId,
              account_name: ctx.accountName,
              source: "dashboard",
            };
            if (isFreshLogin) {
              capturePostHogEvent("user_logged_in", baseProps);
            } else {
              capturePostHogEvent("user_session_resumed", {
                ...baseProps,
                seconds_since_auth: secondsSinceAuth,
              });
            }
          }

          // Track login event (fire-and-forget)
          fetch(`${API_BASE}/api/v1/activity/track`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${keycloak.token}`,
            },
            body: JSON.stringify({
              event: "login",
              source: "dashboard",
              userAgent: navigator.userAgent.slice(0, 500),
            }),
          }).catch(() => { /* best-effort tracking */ });
        }
        setReady(true);
      })
      .catch((err) => {
        clearTimeout(initTimeout);
        console.error("Keycloak init failed", err);
        setReady(true);
      });

    // Wire up authFetch for automatic 401 retry
    if (keycloak) {
      initAuthFetch(keycloak, setToken, () => keycloak!.logout({ redirectUri: appUrl() }));
    }

    // Proactively refresh token every 30s (refresh if expires within 60s)
    const interval = setInterval(() => {
      keycloak.updateToken(60).then((refreshed) => {
        if (refreshed) setToken(keycloak.token);
      }).catch(() => keycloak.login());
    }, 30_000);

    return () => clearInterval(interval);
  }, []);

  const login = useCallback(() => {
    if (PREVIEW_MODE) {
      setAuthenticated(true);
      setToken("fastn_dev_user");
      setUser({ email: "platform_admin@fastn.ai", name: "Platform Admin" });
      setUserContext({
        sub: "dev-admin",
        email: "platform_admin@fastn.ai",
        role: "admin",
        roleResolved: true,
        orgId: "org_acme",
        accountName: "Acme Corp",
        displayName: "Dev Admin",
      });
      // Identify here too, so the preview session behaves like a real one.
      //
      // Without this the bypass sets a user context but never identifies, so
      // `_identified` stays false and EVERY capture() is a silent no-op — which
      // makes the preview build useless for checking that instrumentation
      // fires, and hid exactly that for the whole of this work.
      //
      // Safe to send: the fake user is @fastn.ai, so isInternalEmail() marks it
      // is_internal and the project-level filter excludes it by default.
      identifyPostHogUser({
        id: "dev-admin",
        email: "platform_admin@fastn.ai",
        name: "Dev Admin",
        role: "admin",
        orgId: "org_acme",
        accountName: "Acme Corp",
      });
    } else if (keycloak) {
      keycloak.login();
    }
  }, []);

  const logout = useCallback(async () => {
    // If impersonating, stop impersonation first
    if (impersonating) {
      doStopImpersonation(impersonating);
      return;
    }
    resetPostHog();
    if (PREVIEW_MODE) {
      setAuthenticated(false);
      setToken(undefined);
      setUser(undefined);
      setUserContext(null);
      sessionStorage.clear();
      window.location.href = APP_BASE;
    } else if (keycloak) {
      // Revoke the access token BEFORE handing off to Keycloak.
      //
      // keycloak.logout() ends the SSO session and the refresh token, but the
      // access token already in hand keeps verifying until its own exp — the
      // gateway checks signature and expiry, not session state. Anyone who
      // captured it can keep using it after the user has visibly logged out.
      // This endpoint puts its jti on the revocation denylist; back-channel
      // logout separately kills the whole session server-side.
      //
      // keepalive so the request still completes once logout() navigates away,
      // and non-blocking: a failure here must never trap a user in a session
      // they asked to leave.
      const currentToken = keycloak.token;
      if (currentToken) {
        try {
          await fetch(`${API_BASE}/api/v1/auth/logout`, {
            method: "POST",
            headers: { Authorization: `Bearer ${currentToken}` },
            keepalive: true,
          });
        } catch {
          /* best effort — proceed to Keycloak logout regardless */
        }
      }
      keycloak.logout({ redirectUri: appUrl() });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonating]);

  const switchAccount = useCallback(async (orgId: string) => {
    if (!token) throw new Error("Not authenticated");

    const res = await authFetch(`${API_BASE}/api/v1/users/me/switch-account`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ orgId })
    }, token);
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "Switch failed" }));
      throw new Error((err as Record<string, string>).error ?? "Switch failed");
    }

    const { data } = await res.json() as { data: { orgId: string; role: string; orgName?: string; accountName?: string } };

    // Nothing the client sends scopes a request to an org — the gateway derives
    // it from the org_id claim in the JWT. So the token, not this response, is
    // what decides which org's data comes back, and the UI must not move until
    // the token agrees. Updating userContext first is what made the dashboard
    // show the previous org under the new org's name: every query re-keys to
    // the new orgId and re-fires while the Bearer token still claims the old
    // org, and the reply gets cached under the new org's key.
    if (PREVIEW_MODE) {
      // Dev mode has no Keycloak — swap the dev token so the gateway maps the role.
      const devToken = data.role === "platform_admin" ? "fastn_platform_admin" : "fastn_dev_user";
      setToken(devToken);
    } else if (!AUTH_DISABLED && keycloak?.authenticated) {
      const converged = await waitForOrgClaim(keycloak, data.orgId);
      setToken(keycloak.token);
      if (!converged) {
        // The refreshed token never picked up the new org. Continuing would
        // serve the previous org's data under the new org's name, so force a
        // full reload: re-running the SSO flow mints a token from the now-current
        // Keycloak attributes. The switch is already persisted server-side, so
        // the reload lands in the new org.
        window.location.href = APP_BASE;
        return;
      }
    }

    const switchedMembership = memberships.find((m) => m.orgId === orgId);
    // endOrgId is cleared, not carried: the handler drops end_org_id from the
    // Keycloak attributes on every switch, so a tenant binding stamped by the
    // previous session must not survive here either. It never came back in the
    // response — reading it from there only made the clear look accidental.
    const nextCtx = { orgId: data.orgId, endOrgId: undefined, role: data.role as SystemRole, roleResolved: true, orgType: switchedMembership?.orgType, accountName: data.orgName ?? data.accountName };
    setUserContext((prev) => prev ? { ...prev, ...nextCtx } : prev);

    // Update memberships default flag
    setMemberships((prev) => prev.map((m) => ({
      ...m,
      isDefault: m.orgId === orgId,
    })));

    // Wipe every cached entry — keys include orgId, so the previous org's data
    // would otherwise sit in the cache until gcTime expires. Safe to do here
    // because the token now carries the new org: everything that re-fires is
    // scoped to it.
    queryClient.clear();
  }, [token, memberships]);

  const startImpersonation = useCallback(async (orgId: string, accountName: string) => {
    if (!token || !userContext) throw new Error("Not authenticated");
    if (userContext.role !== "platform_admin") throw new Error("Only platform_admin can impersonate");
    if (!orgId || !orgId.trim()) throw new Error("Account ID is required for impersonation");

    const res = await authFetch(`${API_BASE}/api/v1/impersonate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ orgId, accountName })
    }, token);

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "Impersonation failed" }));
      throw new Error((err as Record<string, string>).error ?? "Impersonation failed");
    }

    const { data } = await res.json() as { data: { impersonatedContext: UserContext; expiresAt: string } };

    const impState: ImpersonationState = {
      orgId,
      accountName,
      originalToken: token,
      originalUserContext: userContext,
      expiresAt: new Date(data.expiresAt).getTime(),
    };

    setImpersonating(impState);
    // Scope all upstream requests to the impersonated account (gateway honors
    // x-impersonate-org for platform_admin — KAN-1820) so JWT-scoped data
    // (workflows, connectors, onboarding…) returns the account's data.
    setImpersonateOrg(orgId);
    const impersonatedCtx = {
      sub: data.impersonatedContext.sub,
      email: data.impersonatedContext.email,
      role: (data.impersonatedContext.role as SystemRole) ?? "admin",
      roleResolved: true,
      orgId: data.impersonatedContext.orgId ?? orgId,
      endOrgId: data.impersonatedContext.endOrgId,
      accountName: data.impersonatedContext.accountName ?? accountName,
      displayName: data.impersonatedContext.displayName,
    };
    setUserContext(impersonatedCtx);
    setUser({
      email: data.impersonatedContext.email,
      name: data.impersonatedContext.displayName,
      sub: data.impersonatedContext.sub,
    });
    dropCachedData();
  }, [token, userContext]);

  // admin impersonating a tenant user (by Keycloak userId)
  const startUserImpersonation = useCallback(async (userId: string, userName: string) => {
    if (!token || !userContext) throw new Error("Not authenticated");

    const res = await authFetch(`${API_BASE}/api/v1/impersonate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ userId })
    }, token);

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "Impersonation failed" }));
      throw new Error((err as Record<string, string>).error ?? "Impersonation failed");
    }

    const { data } = await res.json() as { data: { impersonatedContext: UserContext; expiresAt: string } };

    const impState: ImpersonationState = {
      orgId: data.impersonatedContext.orgId ?? userContext.orgId,
      accountName: data.impersonatedContext.accountName ?? userName,
      originalToken: token,
      originalUserContext: userContext,
      expiresAt: new Date(data.expiresAt).getTime(),
    };

    setImpersonating(impState);
    const impCtx = {
      sub: data.impersonatedContext.sub,
      email: data.impersonatedContext.email,
      role: (data.impersonatedContext.role as SystemRole) ?? "viewer",
      roleResolved: true,
      orgId: data.impersonatedContext.orgId ?? userContext.orgId,
      endOrgId: data.impersonatedContext.endOrgId,
      accountName: data.impersonatedContext.accountName,
      displayName: data.impersonatedContext.displayName,
    };
    setUserContext(impCtx);
    setUser({
      email: data.impersonatedContext.email,
      name: data.impersonatedContext.displayName,
      sub: data.impersonatedContext.sub,
    });
  }, [token, userContext]);

  const stopImpersonation = useCallback(() => {
    if (impersonating) {
      doStopImpersonation(impersonating);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonating]);

  // No UPDATE_PASSWORD: the realm has no password self-service, so Keycloak
  // ignores that kc_action and returns the user straight to /profile.
  const openAccountSecurity = useCallback((action: 'CONFIGURE_TOTP' | 'webauthn-register-passwordless' = 'CONFIGURE_TOTP') => {
    if (PREVIEW_MODE || !keycloak) {
      alert('Security settings are not available in dev mode. Use Keycloak admin console.');
      return;
    }
    // Use keycloak-js login() with action parameter — handles PKCE automatically
    keycloak.login({
      action,
      redirectUri: appUrl('profile'),
    });
  }, []);

  /**
   * The org and role on `userContext` are still a guess, and the retry loop
   * above can still replace them.
   *
   * A brand-new signup's token is minted before provisioning writes the role
   * claim and the org onto the Keycloak user, so `extractUserContext` falls back
   * to role 'viewer' with `roleResolved: false` and `orgId: ""`. Consumers that
   * judge that guess as though it were the answer are what made a first-run
   * account unusable: the role gates denied /agent, /settings and /onboarding,
   * and every query gated on `enabled: !!orgId` — entitlements included — never
   * fired, so the gate that reads them concluded "not entitled" and redirected.
   *
   * Goes false the moment the memberships fetch repairs both, and also once the
   * retries are exhausted, so a user whose org genuinely never resolves falls
   * through to the same answer as before instead of waiting on a spinner.
   */
  const contextPending =
    !PREVIEW_MODE
    && !LINK_TOKEN
    && authenticated
    && membershipAttempt < MEMBERSHIP_MAX_ATTEMPTS
    && (!userContext?.orgId || !userContext.roleResolved);

  return (
    <AuthContext.Provider value={{
      ready: ready && membershipsLoaded, authenticated, contextPending, token, user, userContext,
      memberships, pendingMemberships, accessExpired,
      canSwitchPlatformOrg, orgStatus, retryMemberships, refreshMemberships,
      impersonating,
      login, logout, switchAccount, startImpersonation, startUserImpersonation, stopImpersonation, openAccountSecurity,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
